import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Dropdown, Menu, Modal, ModalProps, Space, Spin, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import moment from 'moment/moment';
import { usePath } from '~/hooks';
import onError from '~/errorsHandler';
import styled from 'styled-components';
import useDirection from '~/hooks/useDirection';
import {
  DownOutlined,
  ExclamationCircleTwoTone,
  WarningTwoTone,
  CheckCircleTwoTone,
} from '@ant-design/icons';
import { UseQueryResult } from 'react-query';
import { ExportCheck, ExportStatus } from '~/types';
import theme from '~/theme';

interface ExportInFileProps {
  useExportCheck: () => UseQueryResult<ExportCheck, unknown>;
  getExportResult: (id: string) => Promise<Blob | string>;
  useCreateExportTask: () => {
    data: { id: string } | undefined;
    mutate: (params: any) => void;
    reset: () => void;
  };
  useExportStatus: (exportId: string | null) => UseQueryResult<ExportStatus, unknown>;
  filePrefix: 'balances' | 'transactions';
  filters?: any;
}

const ExportInFile: FC<ExportInFileProps> = ({
  getExportResult,
  useCreateExportTask,
  useExportStatus,
  useExportCheck,
  filePrefix,
  filters = {},
}) => {
  const { t } = useTranslation();
  const [exportFormat, setExportFormat] = useState<'CSV' | 'XLSX' | null>(null);
  const [modalState, setModalState] = useState<ModalState | null>(null);
  const [dir] = useDirection();
  const { isLoading: isCheckLoading, data: checkResult } = useExportCheck();

  const {
    params: { filter },
  } = usePath();

  // Create the Export task.
  const {
    data: exportTaskData,
    mutate: createExportTask,
    reset: cleanExportTaskData,
  } = useCreateExportTask();
  const exportId = exportTaskData?.id;

  // Ping Export task for status.
  const { data: exportStatus, isLoading } = useExportStatus(exportId!);

  // Download when ready.
  const downloadExportResult = useCallback(async () => {
    if (!exportId) return;
    try {
      const result = await getExportResult(exportId);
      const fileName = `${filePrefix}_${moment().format('L LT')}`;
      if ((navigator as any).msSaveBlob) {
        (navigator as any).msSaveBlob(result, `${fileName}.${exportFormat}`);
      } else {
        const url = window.URL.createObjectURL(new Blob([result]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `${fileName}.${exportFormat}`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (error) {
      onError(error);
    }
  }, [exportId, exportFormat, getExportResult, filePrefix]);

  useEffect(() => {
    if (exportStatus && exportStatus.status === 'DONE') {
      if (exportStatus.statusMessage?.includes(WITH_LIMIT_SUBSTRING)) {
        setModalState('DONE_WITH_ERROR');
      } else {
        setModalState('DONE');
      }
      cleanExportTaskData();
      downloadExportResult();
    }
  }, [exportStatus, downloadExportResult, cleanExportTaskData]);

  const rowsLimitCsv = checkResult?.parameters.rowsLimitCsv;
  const rowsLimitExcel = checkResult?.parameters.rowsLimitExcel;
  const maxRecordsNumber =
    exportFormat === 'CSV' ? rowsLimitCsv : exportFormat === 'XLSX' ? rowsLimitExcel : '';

  const onMenuItemPress = (format: 'CSV' | 'XLSX') => {
    setExportFormat(format);
    if ((rowsLimitCsv && format === 'CSV') || (rowsLimitExcel && format === 'XLSX')) {
      setModalState('CONFIRMATION');
    } else {
      startExport(format);
    }
  };

  const startExport = useCallback(
    (format: 'CSV' | 'XLSX' | null) => {
      if (!format) return;
      setModalState('IN_PROGRESS');
      createExportTask({ ...filter, ...filters, exportFormat: format });
    },
    [createExportTask, filter, filters]
  );

  const onConfirmationPress = useCallback(() => {
    startExport(exportFormat);
  }, [exportFormat, startExport]);

  const onCloseModal = () => {
    cleanExportTaskData();
    setModalState(null);
    setExportFormat(null);
  };

  const modalPropsByState: Record<ModalState, ModalProps> = useMemo(
    () => ({
      CONFIRMATION: {
        title: (
          <Space>
            <ExclamationCircleTwoTone
              className="ModalTitle-Icon"
              twoToneColor={theme.warningColor}
            />
            {t('confirmationExportTitle', { maxRecordsNumber })}
          </Space>
        ),
        children: t('confirmationExportBody', { maxRecordsNumber }),
        onOk: onConfirmationPress,
      },
      IN_PROGRESS: {
        children: <ProcessingContent exportFormat={exportFormat} />,
        okButtonProps: { style: { display: 'none' } },
      },
      DONE: {
        title: (
          <Space>
            <CheckCircleTwoTone className="ModalTitle-Icon" twoToneColor={theme.successColor} />
            {t('exportDone')}
          </Space>
        ),
        children: t('downloadWillStart'),
        okButtonProps: { style: { display: 'none' } },
      },
      DONE_WITH_ERROR: {
        title: (
          <Space>
            <WarningTwoTone className="ModalTitle-Icon" twoToneColor={theme.warningColor} />
            {t('exportDoneLimited', { maxRecordsNumber })}
          </Space>
        ),
        children: t('downloadWillStartLimited', { maxRecordsNumber }),
        okButtonProps: { style: { display: 'none' } },
      },
    }),
    [maxRecordsNumber, exportFormat, onConfirmationPress, t]
  );

  if (isCheckLoading || !checkResult?.available) {
    return null;
  }

  return (
    <>
      <Dropdown
        disabled={isLoading || !!exportId}
        overlay={
          <Menu>
            <Menu.Item key="CSV">
              <div onClick={() => onMenuItemPress('CSV')}>{t('CSV')}</div>
            </Menu.Item>
            <Menu.Item key="XLSX">
              <div onClick={() => onMenuItemPress('XLSX')}>{t('XLSX')}</div>
            </Menu.Item>
          </Menu>
        }
      >
        <Button type="primary" onClick={(e) => e.preventDefault()}>
          <Space>
            {t('exportTo')}
            <DownOutlined />
          </Space>
        </Button>
      </Dropdown>
      <StyledModal
        open={!!modalState}
        closable={false}
        maskClosable={false}
        centered
        dir={dir}
        okText={t('confirm')}
        cancelText={t('close')}
        onCancel={onCloseModal}
        {...modalPropsByState[modalState!]}
      />
    </>
  );
};

type ModalState = 'CONFIRMATION' | 'IN_PROGRESS' | 'DONE' | 'DONE_WITH_ERROR';

const WITH_LIMIT_SUBSTRING = 'export terminated';

interface ProcessingContentProps {
  exportFormat: 'CSV' | 'XLSX' | null;
}

const ProcessingContent: FC<ProcessingContentProps> = ({ exportFormat }) => {
  const { t } = useTranslation();
  return (
    <>
      <Spin size="large" />
      <Typography.Title level={5}>
        {exportFormat && t(`processing${exportFormat}`)}
      </Typography.Title>
    </>
  );
};

interface StyledModalProps extends ModalProps {
  dir: 'ltr' | 'rtl';
}

const StyledModal = styled<FC<StyledModalProps>>(Modal)`
  .ant-modal-body {
    display: flex;
    flex-direction: row;
    align-items: center;
  }

  .ant-typography {
    margin-left: ${({ dir }) => (dir === 'ltr' ? '10px' : '0px')};
    margin-right: ${({ dir }) => (dir === 'rtl' ? '10px' : '0px')};
  }

  .ModalTitle-Icon svg {
    font-size: 22px;
  }
`;

export default ExportInFile;
